import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarClock,
  Check,
  Link2,
  ListChecks,
  Loader2,
  Mic,
  NotebookPen,
  Undo2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MatterCombobox } from "@/components/MatterCombobox";
import { API, api, errText } from "@/lib/api";
import { shortDate } from "@/lib/eventMeta";
import { useAuth } from "@/context/AuthContext";

/** Dictated hearing notes that were filed without review, waiting for a read. */
export const VoiceReviewCard = () => {
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [matters, setMatters] = useState([]);
  const [linking, setLinking] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/logs/review-queue");
      const next = data.logs || [];
      setLogs((prev) => {
        const finished = prev.filter(
          (p) => p.transcribing && !next.find((n) => n.log_id === p.log_id)?.transcribing,
        );
        if (finished.length) {
          toast.success(
            finished.length === 1
              ? "Your dictated note is written up and filed"
              : `${finished.length} dictated notes are written up and filed`,
          );
        }
        return next;
      });
    } catch {
      setLogs([]);
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("diary-refresh", handler);
    return () => window.removeEventListener("diary-refresh", handler);
  }, [load]);

  const writingUp = logs.some((l) => l.transcribing);

  useEffect(() => {
    if (!writingUp) return;
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, [writingUp, load]);

  const openLinker = async (log) => {
    setLinking(log.log_id);
    if (!matters.length) {
      try {
        const { data } = await api.get("/matters");
        setMatters(data);
      } catch {
        setMatters([]);
      }
    }
  };

  const linkTo = async (log, matterId) => {
    if (!matterId) return;
    setBusy(log.log_id);
    try {
      await api.post(`/logs/${log.log_id}/link`, { matter_id: matterId });
      toast.success("Filed on that matter");
      setLinking(null);
      await load();
      window.dispatchEvent(new Event("diary-refresh"));
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(null);
    }
  };

  const makeBrief = async (log) => {
    setBusy(log.log_id);
    try {
      const { data } = await api.post(`/logs/${log.log_id}/brief`, {});
      setLogs((list) =>
        list.map((l) =>
          l.log_id === log.log_id
            ? { ...l, brief_item_ids: data.items.map((i) => i.item_id), brief_date: data.date }
            : l,
        ),
      );
      toast.success(
        data.already
          ? `The brief for ${shortDate(data.date)} is already drawn up`
          : `${data.items.length} instruction${data.items.length === 1 ? "" : "s"} added to the brief for ${shortDate(data.date)}`,
        {
          duration: 10000,
          action: { label: "Open the brief", onClick: () => navigate(`/brief?date=${data.date}`) },
        },
      );
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(null);
    }
  };

  const dropTodo = async (log, todoId) => {
    setBusy(log.log_id);
    try {
      await api.delete(`/logs/${log.log_id}/todos/${todoId}`);
      setLogs((list) =>
        list.map((l) =>
          l.log_id === log.log_id
            ? { ...l, spawned_todos: (l.spawned_todos || []).filter((t) => t.todo_id !== todoId) }
            : l,
        ),
      );
      toast.success("Follow-up dropped");
      window.dispatchEvent(new Event("diary-refresh"));
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(null);
    }
  };

  const setTodoDate = async (log, todoId, patch) => {
    try {
      const { data } = await api.put(`/todos/${todoId}`, patch);
      setLogs((list) =>
        list.map((l) =>
          l.log_id === log.log_id
            ? {
                ...l,
                spawned_todos: (l.spawned_todos || []).map((t) =>
                  t.todo_id === todoId
                    ? { ...t, date: data.date, due_date: data.due_date }
                    : t,
                ),
              }
            : l,
        ),
      );
      window.dispatchEvent(new Event("diary-refresh"));
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const settle = async (log, dropNextDate) => {
    setBusy(log.log_id);
    try {
      await api.post(`/logs/${log.log_id}/reviewed`, { drop_next_date: dropNextDate });
      toast.success(dropNextDate ? "Read, and the next date was removed" : "Marked as read");
      setLogs((list) => list.filter((l) => l.log_id !== log.log_id));
      window.dispatchEvent(new Event("diary-refresh"));
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(null);
    }
  };

  if (!logs.length) return null;

  const audioUrl = (log) =>
    `${API}/logs/${log.log_id}/audio` +
    (workspace?.workspace_id ? `?workspace_id=${workspace.workspace_id}` : "");

  return (
    <section className="surface p-6" data-testid="voice-review-card">
      <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
        <Mic className="h-5 w-5" /> Dictated notes to read · {logs.length}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Filed straight onto the hearing from your dictation. Read it, and tick it off.
      </p>

      <div className="mt-4 space-y-3">
        {logs.slice(0, 6).map((log) => (
          <div
            key={log.log_id}
            data-testid={`voice-review-${log.log_id}`}
            className="border border-border rounded-sm p-4"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="label-caps">{shortDate(log.date)}</span>
              {log.next_date ? (
                <span
                  data-testid={`voice-review-next-${log.log_id}`}
                  className="chip chip-session inline-flex items-center gap-1.5"
                >
                  <CalendarClock className="h-3.5 w-3.5" /> Next {shortDate(log.next_date)}
                </span>
              ) : null}
            </div>
            <p className="mt-1.5 text-base font-semibold leading-snug break-words">
              {log.matter_title || (log.needs_matter ? "Which matter?" : "Unlinked hearing")}
            </p>
            {!log.transcribing && log.matter_hint && !log.needs_matter ? (
              <p
                data-testid={`voice-review-guessed-${log.log_id}`}
                className="mt-1 text-xs text-muted-foreground"
              >
                Filed from what was heard: "{log.matter_hint}"
              </p>
            ) : null}
            {log.needs_matter && !log.transcribing ? (
              <p
                data-testid={`voice-review-needs-matter-${log.log_id}`}
                className="mt-1 text-xs text-muted-foreground"
              >
                {log.matter_hint
                  ? `Heard "${log.matter_hint}" but nothing matched — link it below.`
                  : "The matter could not be worked out — link it below."}
              </p>
            ) : null}
            {log.transcribing ? (
              <p
                data-testid={`voice-review-writing-${log.log_id}`}
                className="mt-2 inline-flex items-center gap-2 text-sm text-muted-foreground"
              >
                <Loader2 className="h-4 w-4 animate-spin" /> Being written up…
              </p>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{log.preview}</p>
            )}

            {log.has_audio ? (
              <audio
                controls
                preload="none"
                src={audioUrl(log)}
                data-testid={`voice-review-audio-${log.log_id}`}
                className="mt-3 h-9 w-full"
              />
            ) : null}

            {log.spawned_todos?.length ? (
              <div
                className="mt-3 border-t border-border pt-3"
                data-testid={`voice-review-todos-${log.log_id}`}
              >
                <p className="label-caps inline-flex items-center gap-1.5">
                  <ListChecks className="h-3.5 w-3.5" /> Follow-ups created ·{" "}
                  {log.spawned_todos.length}
                </p>
                <ul className="mt-2 space-y-2">
                  {log.spawned_todos.map((t) => (
                    <li
                      key={t.todo_id}
                      data-testid={`voice-review-todo-${t.todo_id}`}
                      className="flex flex-wrap items-start gap-2 text-sm"
                    >
                      <span className="font-mono text-xs mt-2">[ ]</span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium break-words">{t.title}</span>
                        <span className="mt-1 flex flex-wrap items-center gap-2">
                          <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                            Shows from
                            <input
                              type="date"
                              data-testid={`voice-review-todo-start-${t.todo_id}`}
                              value={t.date || ""}
                              onChange={(e) =>
                                e.target.value &&
                                setTodoDate(log, t.todo_id, { date: e.target.value })
                              }
                              className="h-8 rounded-sm border border-input bg-card px-2 text-xs"
                            />
                          </label>
                          <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                            Due by
                            <input
                              type="date"
                              data-testid={`voice-review-todo-due-${t.todo_id}`}
                              value={t.due_date || ""}
                              min={t.date || undefined}
                              onChange={(e) =>
                                setTodoDate(log, t.todo_id, { due_date: e.target.value || null })
                              }
                              className="h-8 rounded-sm border border-input bg-card px-2 text-xs"
                            />
                          </label>
                        </span>
                      </span>
                      <button
                        data-testid={`voice-review-todo-drop-${t.todo_id}`}
                        aria-label="Drop this follow-up"
                        onClick={() => dropTodo(log, t.todo_id)}
                        className="h-7 w-7 grid place-items-center rounded-sm hover:bg-destructive/10 hover:text-destructive transition-colors duration-200"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {linking === log.log_id ? (
              <div className="mt-3" data-testid={`voice-review-linker-${log.log_id}`}>
                <MatterCombobox
                  matters={matters}
                  value={null}
                  onChange={(id) => linkTo(log, id)}
                  testId={`voice-review-matter-${log.log_id}`}
                  emptyLabel="Pick the matter"
                  disabled={busy === log.log_id}
                />
              </div>
            ) : null}

            <div className="mt-3 flex flex-wrap gap-2">
              {!log.transcribing && (log.needs_matter || log.matter_hint) ? (
                <Button
                  variant="outline"
                  data-testid={`voice-review-link-${log.log_id}`}
                  onClick={() => openLinker(log)}
                  className="rounded-full h-10 px-4 text-xs"
                >
                  <Link2 className="h-4 w-4 mr-1.5" />
                  {log.needs_matter ? "Link to a matter" : "Change matter"}
                </Button>
              ) : null}
              <Button
                variant="outline"
                data-testid={`voice-review-open-${log.log_id}`}
                onClick={() =>
                  navigate(
                    log.matter_id
                      ? `/matters/${log.matter_id}`
                      : `/calendar?date=${log.date}&view=day`,
                  )
                }
                className="rounded-full h-10 px-4 text-xs"
              >
                Read the full note
              </Button>
              {log.transcribing ? null : log.brief_item_ids?.length ? (
                <Button
                  variant="outline"
                  data-testid={`voice-review-brief-open-${log.log_id}`}
                  onClick={() => navigate(`/brief?date=${log.brief_date}`)}
                  className="rounded-full h-10 px-4 text-xs"
                >
                  <NotebookPen className="h-4 w-4 mr-1.5" /> Brief ready ·{" "}
                  {shortDate(log.brief_date)}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  data-testid={`voice-review-brief-${log.log_id}`}
                  disabled={busy === log.log_id}
                  onClick={() => makeBrief(log)}
                  className="rounded-full h-10 px-4 text-xs"
                >
                  <NotebookPen className="h-4 w-4 mr-1.5" /> Brief the associate
                </Button>
              )}
              <Button
                data-testid={`voice-review-done-${log.log_id}`}
                disabled={busy === log.log_id || log.transcribing}
                onClick={() => settle(log, false)}
                className="rounded-full h-10 px-4 text-xs"
              >
                <Check className="h-4 w-4 mr-1.5" /> Mark read
              </Button>
              {log.next_date_from_voice ? (
                <Button
                  variant="outline"
                  data-testid={`voice-review-undo-${log.log_id}`}
                  disabled={busy === log.log_id}
                  onClick={() => settle(log, true)}
                  className="rounded-full h-10 px-4 text-xs text-destructive border-destructive/40 hover:bg-destructive/10"
                >
                  <Undo2 className="h-4 w-4 mr-1.5" /> Undo next date
                </Button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
